import { ScrollingModule } from '@angular/cdk/scrolling';
import { NestedTreeControl } from '@angular/cdk/tree';
import { DatePipe, DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  model,
  output,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { AIDocumentationCategories, SiaAIChatService } from '@sia/ai';
import {
  SiaDateTimeBoxComponent,
  SiaNumberboxComponent,
  SiaTextBoxComponent,
} from '@sia/inputs';
import {
  jsonDeepClone,
  SiaSearchBoxComponent,
  SiaSelectBoxDataService,
  SiaSizeDetectorService,
  SiaTooltipDirective,
  SiaTranslatePipe,
} from '@sia/shared';
import {
  SiaGridFilter,
  SiaGridFilterChip,
  SiaGridFilterRequest,
} from '../../interfaces/ai-filters';
import { SiaAIFiltersKeys } from '../../interfaces/ai-filters-keys';
import { SiaAIFilesService } from '../../services/ai-files.service';

@Component({
  selector: 'sia-ai-grid-filter',
  imports: [
    SiaSearchBoxComponent,
    NgTemplateOutlet,
    MatMenuModule,
    MatTreeModule,
    MatIcon,
    MatCheckbox,
    SiaTextBoxComponent,
    MatIconButton,
    SiaNumberboxComponent,
    SiaDateTimeBoxComponent,
    MatMenuTrigger,
    SiaTooltipDirective,
    SiaTranslatePipe,
    MatButton,
    ScrollingModule,
  ],

  templateUrl: './ai-grid-filter.component.html',
  styleUrl: './ai-grid-filter.component.scss',
})
export class AiGridFilterComponent {
  elementRef = inject(ElementRef);
  queryText = model<string | undefined>(undefined);
  selectedCategoryIds = model<Set<string>>(new Set());
  aiGeneratedFilters = model<{
    filters: SiaGridFilterRequest[];
    categories: string[];
  }>();

  #aiChatService = inject(SiaAIChatService);
  #datePipe = inject(DatePipe);
  #siaSelectBoxDataService = inject(SiaSelectBoxDataService);
  #siaAIFilesService = inject(SiaAIFilesService);
  #siaSizeDetectorService = inject(SiaSizeDetectorService);
  #document = inject(DOCUMENT);

  readonly id = Math.random().toString(36).slice(2);
  readonly svgUrlPrefix = this.#document.location.pathname;

  // AI generated filters
  aiFilterChips = signal<SiaGridFilterChip[]>([]); // ----
  #aiSelectedCategories = signal<Set<string>>(new Set());

  documentationCategories = signal<AIDocumentationCategories[]>([]);
  keysAdditionalFilters = signal<SiaAIFiltersKeys[]>([]);
  keysFilterChips = signal<SiaGridFilterChip[]>([]);
  generalFilterChips = signal<SiaGridFilterChip[]>([]);
  filtersChips = signal<SiaGridFilterChip[]>([
    {
      menuTriggerType: 'array',
      tooltip: 'Tipologia',
      label: 'Tipologia',
      arrayTemplateKey: 'fs.v_filter_files_types',
      menuData: {
        filter: {
          key: 'type',
          arrayFilter: [],
        },
      },
    },
    {
      menuTriggerType: 'text',
      tooltip: 'Nome',
      label: 'Nome',
      menuData: {
        filter: {
          key: 'name',
          textFilter: '',
        },
      },
    },
    {
      menuTriggerType: 'text',
      tooltip: 'Percorso',
      label: 'Percorso',
      menuData: {
        filter: {
          key: 'path',
          textFilter: '',
        },
      },
    },
    {
      menuTriggerType: 'date',
      tooltip: 'Data ult. modifica',
      label: 'Data ult. modifica',
      menuData: {
        filter: {
          key: 'lastModified',
        },
      },
    },
  ]);

  // Tree control for nested categories
  treeControl = new NestedTreeControl<AIDocumentationCategories>(
    (node) => node.children,
  );
  categoriesTreeDataSource =
    new MatTreeNestedDataSource<AIDocumentationCategories>();
  aiCategoriesTreeDataSource =
    new MatTreeNestedDataSource<AIDocumentationCategories>();

  indeterminateCategories = computed<{ [key: string]: boolean }>(() => {
    const indeterminateCategories: { [key: string]: boolean } = {};
    this.selectedCategoryIds();
    this.documentationCategories().forEach((category) => {
      const children = category.children || [];
      const checkedChildren = children.filter((child: any) =>
        this.selectedCategoryIds().has(child.id),
      );
      indeterminateCategories[category.id] =
        checkedChildren.length > 0 && checkedChildren.length < children.length;
    });
    return indeterminateCategories;
  });
  allCategoryChildrenSelected = computed<{ [key: string]: boolean }>(() => {
    const allChildrenSelected: { [key: string]: boolean } = {};
    this.selectedCategoryIds();
    this.documentationCategories().forEach((category) => {
      const children = category.children || [];
      const checkedChildren = children.filter((child: any) =>
        this.selectedCategoryIds().has(child.id),
      );
      allChildrenSelected[category.id] =
        checkedChildren.length === children.length;
    });
    return allChildrenSelected;
  });

  allCategoriesState = computed(() => {
    let checkedCount = 0;
    let totalLeaves = 0;

    totalLeaves += 1;
    if (this.selectedCategoryIds().has('none-category')) {
      checkedCount++;
    }

    this.documentationCategories().forEach((category) => {
      const children = category.children || [];
      if (children.length > 0) {
        totalLeaves += children.length;
        children.forEach((child: any) => {
          if (this.selectedCategoryIds().has(child.id)) {
            checkedCount++;
          }
        });
      } else {
        totalLeaves += 1;
        if (this.selectedCategoryIds().has(category.id)) {
          checkedCount++;
        }
      }
    });

    return {
      checked: totalLeaves > 0 && checkedCount === totalLeaves,
      indeterminate: checkedCount > 0 && checkedCount < totalLeaves,
    };
  });

  toggleAllCategories($event: MatCheckboxChange): void {
    if (!$event.checked) {
      this.selectedCategoryIds.set(new Set());
    } else {
      const selected = new Set<string>(this.selectedCategoryIds());
      selected.add('none-category');
      this.documentationCategories().forEach((category) => {
        const children = category.children || [];
        if (children.length > 0) {
          children.forEach((child: any) => selected.add(child.id));
        } else {
          selected.add(category.id);
        }
      });
      this.selectedCategoryIds.set(selected);
    }
  }
  keysAdditionalFiltersByCategory = computed(() => {
    let categoriesValue = this.keysAdditionalFilters().reduce(
      (acc, keyFilter) => {
        const category = keyFilter.categoriesObject[0];
        if (!acc[category.id!]) {
          acc[category.id!] = [];
        }
        acc[category.id!].push(keyFilter);
        return acc;
      },
      {} as Record<string, SiaAIFiltersKeys[]>,
    );
    let categoriesGroup: {
      id: string;
      description: string;
      keys: SiaAIFiltersKeys[];
    }[] = [];
    Object.keys(categoriesValue).forEach((key) => {
      categoriesGroup.push({
        id: key,
        description: categoriesValue[key][0].categoriesObject[0].description!,
        keys: categoriesValue[key],
      });
    });
    return categoriesGroup;
  });

  filterChanged = computed(() => {
    let currentFiltersJson = JSON.stringify(this.requestFilters());
    let currentSelectedCategoryIdsJson = JSON.stringify(
      Array.from(this.selectedCategoryIds()),
    );
    return (
      currentFiltersJson !== this.#previousFilters ||
      (this.queryText() ?? '') !== (this.#previousQueryText ?? '') ||
      currentSelectedCategoryIdsJson !== this.#previousCategories
    );
  });

  requestFilters = computed(() => {
    let filterChipsToUpdate = [
      ...this.filtersChips(),
      ...this.keysFilterChips(),
      ...this.generalFilterChips(),
    ];
    let filterRequests: SiaGridFilterRequest[] = [];
    filterChipsToUpdate.forEach((filterChip) => {
      switch (filterChip.menuTriggerType) {
        case 'array':
          if (
            !filterChip.menuData.filter.arrayFilter ||
            filterChip.menuData.filter.arrayFilter.length === 0
          ) {
            return;
          }
          break;
        case 'date':
          if (
            !filterChip.menuData.filter.dateStart &&
            !filterChip.menuData.filter.dateEnd
          ) {
            return;
          }
          break;
        case 'text':
          if (!filterChip.menuData.filter.textFilter) {
            return;
          }
          break;
        case 'number':
          if (
            filterChip.menuData.filter.numberFilter === null ||
            filterChip.menuData.filter.numberFilter === undefined
          ) {
            return;
          }
          break;
      }
      filterRequests.push({
        key: filterChip.menuData.filter.key,
        type: filterChip.menuTriggerType,
        textFilter: filterChip.menuData.filter.textFilter,
        textOperator: filterChip.menuData.filter.textOperator,
        arrayFilter: filterChip.menuData.filter.arrayFilter,
        dateStart: filterChip.menuData.filter.dateStart,
        dateEnd: filterChip.menuData.filter.dateEnd,
        isCategoryFilter: filterChip.menuData.filter.isCategoryFilter,
        categoryId: filterChip.menuData.filter.categoryId,
        numberFilter: filterChip.menuData.filter.numberFilter,
        numberOperator: filterChip.menuData.filter.numberOperator,
      });
    });
    return filterRequests;
  });

  #convertAiGeneratedFiltersToFilterChipsEffect = effect(async () => {
    this.aiGeneratedFilters();
    this.filtersChips();
    this.keysFilterChips();
    untracked(async () => {
      let aiGeneratedFilters = this.aiGeneratedFilters();
      let aiFilterChips: SiaGridFilterChip[] = [];
      aiGeneratedFilters?.filters?.forEach((aiGeneratedFilter) => {
        let filterChip = [
          ...this.filtersChips(),
          ...this.keysFilterChips(),
        ].find((x) => x.menuData.filter.key === aiGeneratedFilter.key);
        if (filterChip) {
          let aiFilterChip: SiaGridFilterChip = {
            ...filterChip,
            menuData: {
              ...filterChip.menuData,
              filter: {
                ...filterChip.menuData.filter,
                textFilter: aiGeneratedFilter.textFilter,
                textOperator: aiGeneratedFilter.textOperator,
                arrayFilter: aiGeneratedFilter.arrayFilter,
                dateStart: aiGeneratedFilter.dateStart,
                dateEnd: aiGeneratedFilter.dateEnd,
                isCategoryFilter: aiGeneratedFilter.isCategoryFilter,
                categoryId: aiGeneratedFilter.categoryId,
                numberFilter: aiGeneratedFilter.numberFilter,
                numberOperator: aiGeneratedFilter.numberOperator,
              },
            },
          };
          if (
            filterChip.applied &&
            ['text', 'number', 'date'].includes(filterChip.menuTriggerType)
          ) {
            aiFilterChip.inConflict = true;
          }
          if (!this.chipsHaveSameValueBasedOnType(aiFilterChip, filterChip)) {
            aiFilterChips.push(aiFilterChip);
          }
        }
      });

      if (
        !this.documentationCategories() ||
        this.documentationCategories().length === 0
      ) {
        await this.loadDocumentationCategories();
      }
      let aiCategoriesForDatasource: AIDocumentationCategories[] =
        jsonDeepClone(this.documentationCategories());
      this.#aiSelectedCategories.set(new Set());
      aiCategoriesForDatasource = this.removeCategoriesNotInSelection(
        aiGeneratedFilters?.categories,
        aiCategoriesForDatasource,
      );
      this.aiCategoriesTreeDataSource.data = aiCategoriesForDatasource;
      if (this.#siaSizeDetectorService.currentSize.isMobile) {
        if (
          (aiFilterChips?.length ?? 0) > 0 ||
          (aiGeneratedFilters?.categories?.length ?? 0) > 0
        ) {
          this.applyAIFilters(aiFilterChips);
        }
      } else {
        this.aiFilterChips.set(aiFilterChips);
        this.setFilterChipsTextDescriptions(this.aiFilterChips);
      }
    });
  });

  #chipArrayEffect = effect(async () => {
    this.filtersChips();
    let filterChipsToUpdate = this.filtersChips();
    let dataSourcePromises: Promise<any>[] = [];
    let arrayFilters = filterChipsToUpdate.filter(
      (x) => !!x.arrayTemplateKey && !x.menuData.arrayDataSource,
    );
    if (arrayFilters && arrayFilters.length > 0) {
      untracked(async () => {
        arrayFilters.forEach((arrayFilter) => {
          arrayFilter.menuTriggerType = 'array';
          let dataSourcePromise = this.#siaSelectBoxDataService
            .loadFullData(
              arrayFilter.arrayTemplateKey,
              '',
              true,
              false,
              true,
              false,
              undefined,
              undefined,
              'ai',
            )
            .then((filterDataSurce) => {
              arrayFilter.menuData.arrayDataSource = filterDataSurce;
            });
          dataSourcePromises.push(dataSourcePromise);
        });
        await Promise.all(dataSourcePromises);
        untracked(() => {
          this.filtersChips.set([...filterChipsToUpdate]);
        });
      });
    }
  });

  #chipArrayGeneralEffect = effect(async () => {
    this.generalFilterChips();
    let filterChipsToUpdate = this.generalFilterChips();
    let dataSourcePromises: Promise<any>[] = [];
    let arrayFilters = filterChipsToUpdate
      .filter((x) => x.menuTriggerType === 'array')
      .filter((x) => !!x.arrayTemplateKey && !x.menuData.arrayDataSource);
    if (arrayFilters && arrayFilters.length > 0) {
      untracked(async () => {
        arrayFilters.forEach((arrayFilter) => {
          let dataSourcePromise = this.#siaSelectBoxDataService
            .loadFullData(
              arrayFilter.arrayTemplateKey,
              '',
              true,
              false,
              true,
              false,
              undefined,
              undefined,
              'ai',
            )
            .then((filterDataSurce) => {
              arrayFilter.menuData.arrayDataSource = filterDataSurce;
            });
          dataSourcePromises.push(dataSourcePromise);
        });
        await Promise.all(dataSourcePromises);
        this.generalFilterChips.set([...filterChipsToUpdate]);
      });
    }
  });

  #categoryChangeEffect = effect(async () => {
    this.selectedCategoryIds();
    let keysFilterKeys = await this.#siaAIFilesService.retriveKeysFilterChips(
      this.selectedCategoryIds(),
    );
    keysFilterKeys.forEach((keyFilterChip) => {
      keyFilterChip.typeIcon = getKeyIconByType(keyFilterChip.type);
      keyFilterChip.categoriesObject = JSON.parse(
        keyFilterChip.categories as string,
      );
    });
    keysFilterKeys = keysFilterKeys.sort((a, b) =>
      (a.categoriesObject[0]!.description ?? '').localeCompare(
        b.categoriesObject[0]!.description ?? '',
      ),
    );
    untracked(() => {
      this.keysAdditionalFilters.update((keysFilters) => {
        let stillPresentKeys = keysFilters.filter((x) =>
          keysFilterKeys.some(
            (y) =>
              y.key === x.key &&
              y.categoriesObject[0].id === x.categoriesObject[0].id,
          ),
        );
        let newKeys = keysFilterKeys.filter(
          (x) =>
            !keysFilters.some(
              (y) =>
                y.key === x.key &&
                y.categoriesObject[0].id === x.categoriesObject[0].id,
            ),
        );
        return [...stillPresentKeys, ...newKeys].sort((a, b) =>
          a.description.localeCompare(b.description),
        );
      });
      this.keysFilterChips.update((keysFilterChips) => {
        let stillPresentKeys = keysFilterChips.filter((x) =>
          keysFilterKeys.some(
            (y) =>
              y.key === x.menuData.filter.key &&
              y.categoriesObject[0].id === x.menuData.filter.categoryId,
          ),
        );
        return [...stillPresentKeys];
      });
    });
  });

  readonly numberOperators = [
    { value: 'eq', icon: 'dx-icon-equal', label: 'Uguale' },
    { value: 'neq', icon: 'dx-icon-notequal', label: 'Diverso' },
    { value: 'gt', icon: 'dx-icon-greater', label: 'Maggiore' },
    {
      value: 'gte',
      icon: 'dx-icon-greaterorequal',
      label: 'Maggiore o uguale',
    },
    { value: 'lt', icon: 'dx-icon-less', label: 'Minore' },
    { value: 'lte', icon: 'dx-icon-lessorequal', label: 'Minore o uguale' },
  ];

  readonly textOperators = [
    { value: 'contains', icon: 'dx-icon-contains', label: 'Contiene' },
    {
      value: 'notcontains',
      icon: 'dx-icon-doesnotcontain',
      label: 'Non contiene',
    },
    { value: 'startswith', icon: 'dx-icon-startswith', label: 'Inizia con' },
    { value: 'endswith', icon: 'dx-icon-endswith', label: 'Finisce con' },
    { value: 'eq', icon: 'dx-icon-equal', label: 'Uguale' },
    { value: 'neq', icon: 'dx-icon-notequal', label: 'Diverso' },
  ];
  #previousFilters: string = '[]';
  #previousCategories: string = '[]';
  applyFiltersEvent = output<{
    filters: SiaGridFilterRequest[];
    categories: string[];
  }>();
  #previousQueryText: string = '';

  /**
   *
   */
  async ngOnInit() {
    let generalFilters = await this.#siaAIFilesService.retriveKeysFilterChips(
      new Set<string>(['general']),
    );
    console.log('Filtri generali', generalFilters);
    generalFilters.forEach((generalFilter) => {
      let chipType: 'array' | 'number' | 'text' | 'date' =
        this.retriveChipTypeByFilter(generalFilter);

      this.generalFilterChips.update(
        (generalFilterChips: SiaGridFilterChip[]) => {
          return [
            ...generalFilterChips,
            {
              menuTriggerType: chipType,
              tooltip: generalFilter.description,
              label: generalFilter.description,
              category: '',
              arrayTemplateKey: generalFilter.arrayTemplateKey,
              menuData: {
                filter: {
                  key: generalFilter.key,
                  isCategoryFilter: true,
                  categoryId: 'general',
                  numberOperator: chipType === 'number' ? 'eq' : undefined,
                  arrayFilter: chipType == 'array' ? [] : undefined,
                },
              },
            },
          ];
        },
      );
    });
    console.log('Filtri generali', this.generalFilterChips());
  }
  applyAIFilter(filter: SiaGridFilter, $event: PointerEvent) {}
  removeAIFilter(filter: SiaGridFilter, $event: PointerEvent) {
    this.aiFilterChips.update((chips) => [
      ...chips.filter(
        (chip) =>
          chip.menuData.filter.key !== filter.key ||
          chip.menuData.filter.categoryId !== filter.categoryId,
      ),
    ]);
  }

  getOperatorIcon(value: string | undefined): string {
    return (
      this.numberOperators.find((op) => op.value === (value || 'eq'))?.icon ||
      'dx-icon-equal'
    );
  }

  getOperatorLabel(value: string | undefined): string {
    return (
      this.numberOperators.find((op) => op.value === (value || 'eq'))?.label ||
      'Uguale'
    );
  }

  getTextOperatorIcon(value: string | undefined): string {
    return (
      this.textOperators.find((op) => op.value === (value || 'contains'))
        ?.icon || 'dx-icon-filter'
    );
  }

  getTextOperatorLabel(value: string | undefined): string {
    return (
      this.textOperators.find((op) => op.value === (value || 'contains'))
        ?.label || 'Contiene'
    );
  }

  async loadDocumentationCategories() {
    if (this.documentationCategories().length === 0) {
      const categories =
        (await this.#aiChatService.getDocumentationCategories()) || [];

      this.documentationCategories.set(categories);
      this.categoriesTreeDataSource.data = categories;

      // Expand all parent nodes by default
      categories.forEach((parent) => {
        if (parent.children && parent.children.length > 0) {
          this.treeControl.expand(parent);
        }
      });
    }
  }

  removeCategoryFilter() {
    this.selectedCategoryIds.set(new Set());
  }

  isChecked(categoryId: string): boolean {
    return this.selectedCategoryIds().has(categoryId);
  }

  toggleCategory(categoryId: string): void {
    const selected = new Set(this.selectedCategoryIds());

    if (selected.has(categoryId)) {
      selected.delete(categoryId);
    } else {
      selected.add(categoryId);
    }
    this.selectedCategoryIds.set(selected);
  }

  removeCategoriesNotInSelection(
    categories: string[] | undefined,
    aiCategoriesForDatasource: AIDocumentationCategories[],
  ): AIDocumentationCategories[] {
    aiCategoriesForDatasource.forEach((category) => {
      if (category.children) {
        let originalChildrenLength = (category.children ?? []).length;
        category.children = this.removeCategoriesNotInSelection(
          categories,
          category.children,
        );
        if (originalChildrenLength == (category.children ?? []).length) {
          category.indeterminate = false;
        } else {
          category.indeterminate = true;
        }
      }
    });
    if (!aiCategoriesForDatasource || aiCategoriesForDatasource.length === 0) {
      return aiCategoriesForDatasource;
    }
    let hasChildren = aiCategoriesForDatasource.some(
      (category) => (category.children ?? [])!.length > 0,
    );
    aiCategoriesForDatasource = aiCategoriesForDatasource.filter(
      (category) =>
        categories?.includes(category.id!.replace('_only-', '')) ||
        (category.children ?? [])!.length > 0,
    );
    if (!hasChildren) {
      this.#aiSelectedCategories.set(
        new Set([
          ...this.#aiSelectedCategories(),
          ...aiCategoriesForDatasource.map((category) => category.id!),
        ]),
      );
    }
    return aiCategoriesForDatasource;
  }

  toggleParentCategory(parentCategory: any, $event: MatCheckboxChange): void {
    const selected = new Set(this.selectedCategoryIds());
    const children = parentCategory.children || [];

    // Check if all children are selected
    const allChildrenSelected = children.every((child: any) =>
      selected.has(child.id),
    );

    if (allChildrenSelected || !$event.checked) {
      // Uncheck parent and all children
      children.forEach((child: any) => selected.delete(child.id));
    } else {
      // Check parent and all children
      children.forEach((child: any) => selected.add(child.id));
    }

    this.selectedCategoryIds.set(selected);
  }

  hasChild = (_: number, node: AIDocumentationCategories) =>
    !!node.children && node.children.length > 0;

  clearCategorySelection() {
    this.selectedCategoryIds.set(new Set());
  }

  toggleArrayFilter(filter: SiaGridFilter, id: any) {
    filter.arrayFilter = filter.arrayFilter || [];
    const index = filter.arrayFilter.indexOf(id);
    if (index > -1) {
      filter.arrayFilter = filter.arrayFilter.filter((item) => item !== id);
    } else {
      filter.arrayFilter.push(id);
    }
    this.filtersChips.set([...this.filtersChips()]);
  }

  removeFilter(filter: SiaGridFilter, $event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
    if (!filter) {
      return;
    }
    filter.textFilter = undefined;
    filter.textOperator = undefined;
    filter.dateEnd = undefined;
    filter.dateStart = undefined;
    filter.arrayFilter = [];
    filter.numberFilter = undefined;
    this.updateSignalValues();
    this.setAllFilterChipsTextDescriptions();
  }
  setAllFilterChipsTextDescriptions() {
    this.setFilterChipsTextDescriptions(this.filtersChips);
    this.setFilterChipsTextDescriptions(this.keysFilterChips);
    this.setFilterChipsTextDescriptions(this.generalFilterChips);
  }
  setFilterTextValue(filter: SiaGridFilter, value: string) {
    filter.textFilter = value;
    this.updateSignalValues();
  }

  setFilterNumberValue(filter: SiaGridFilter, value: number) {
    console.log('Numero cambiato', value);
    filter.numberFilter = value;
    this.updateSignalValues();
  }
  updateSignalValues() {
    this.filtersChips.set([...this.filtersChips()]);
    this.keysFilterChips.set([...this.keysFilterChips()]);
    this.generalFilterChips.set([...this.generalFilterChips()]);
  }

  setFilterNumberOperator(
    filter: SiaGridFilter,
    value: string,
    $event: Event,
    trigger: MatMenuTrigger,
  ) {
    $event.preventDefault();
    $event.stopPropagation();
    filter.numberOperator = value;
    this.updateSignalValues();
    trigger.closeMenu();
  }

  setFilterTextOperator(
    filter: SiaGridFilter,
    value: string,
    $event: Event,
    trigger: MatMenuTrigger,
  ) {
    $event.preventDefault();
    $event.stopPropagation();
    filter.textOperator = value;
    this.updateSignalValues();
    trigger.closeMenu();
  }

  setFilterDateStartValue(filter: SiaGridFilter, $event: any) {
    filter.dateStart = $event;
    this.updateSignalValues();
  }

  setFilterDateEndValue(filter: SiaGridFilter, $event: any) {
    filter.dateEnd = $event;
    this.updateSignalValues();
  }

  private setFilterChipsTextDescriptions(
    chipsSignal: WritableSignal<SiaGridFilterChip[]>,
  ) {
    let filterChips = chipsSignal();
    filterChips.forEach((filterChip) => {
      filterChip.applied = false;
      filterChip.filterText = '';
      switch (filterChip.menuTriggerType) {
        case 'array':
          if (
            !filterChip.menuData.filter.arrayFilter ||
            filterChip.menuData.filter.arrayFilter.length === 0
          ) {
            return;
          }
          break;
        case 'date':
          if (
            !filterChip.menuData.filter.dateStart &&
            !filterChip.menuData.filter.dateEnd
          ) {
            return;
          }
          break;
        case 'text':
          if (!filterChip.menuData.filter.textFilter) {
            return;
          }
          break;
        case 'number':
          if (
            filterChip.menuData.filter.numberFilter == null ||
            filterChip.menuData.filter.numberFilter == undefined
          ) {
            return;
          }
          break;
      }
      filterChip.applied = true;
      switch (filterChip.menuTriggerType) {
        case 'array':
          const selectedItems = (
            filterChip.menuData.filter.arrayFilter || []
          ).map((id: any) => {
            const item = (filterChip.menuData.arrayDataSource || []).find(
              (x: any) => x.id === id,
            );
            return item ? item.description : id;
          });
          const text = selectedItems.join(', ') || '';
          filterChip.filterText =
            text.length > 30 ? text.slice(0, 30) + '...' : text;
          break;
        case 'date':
          const dateStartText = filterChip.menuData.filter.dateStart
            ? 'Da ' +
              this.#datePipe.transform(
                filterChip.menuData.filter.dateStart,
                'dd/MM/yyyy',
              )
            : '';
          const dateEndText = filterChip.menuData.filter.dateEnd
            ? 'A ' +
              this.#datePipe.transform(
                filterChip.menuData.filter.dateEnd,
                'dd/MM/yyyy',
              )
            : '';
          filterChip.filterText =
            dateStartText +
            (dateStartText && dateEndText
              ? ' ' + dateEndText
              : dateEndText
                ? dateEndText
                : '');
          break;
        case 'text':
          const textFilter = filterChip.menuData.filter.textFilter || '';
          const textOpObj = this.textOperators.find(
            (op) =>
              op.value ===
              (filterChip.menuData.filter.textOperator || 'contains'),
          );
          const tOpLabel =
            textOpObj && textOpObj.value !== 'contains'
              ? textOpObj.label + ' '
              : '';
          const fullDisplayedText = tOpLabel + textFilter;
          filterChip.filterText =
            fullDisplayedText.length > 20
              ? fullDisplayedText.slice(0, 20) + '...'
              : fullDisplayedText;
          break;
        case 'number':
          console.log(
            'Numero cambiato',
            filterChip.menuData.filter.numberFilter,
          );
          const op = this.numberOperators.find(
            (op) => op.value === filterChip.menuData.filter.numberOperator,
          );
          const opLabel = op ? (op.value === 'eq' ? '' : op.label + ' ') : '';
          filterChip.filterText =
            opLabel + filterChip.menuData.filter.numberFilter;
          break;
      }
    });
    chipsSignal.set([...filterChips]);
  }

  applyFilters() {
    let requestFilters = JSON.stringify(this.requestFilters());
    let requestCategories = JSON.stringify(
      Array.from(this.selectedCategoryIds()),
    );
    if (
      requestFilters === this.#previousFilters &&
      requestCategories === this.#previousCategories &&
      this.queryText() === this.#previousQueryText
    ) {
      return;
    }
    this.#previousFilters = requestFilters;
    this.#previousCategories = requestCategories;
    this.#previousQueryText = this.queryText() ?? '';
    this.setAllFilterChipsTextDescriptions();
    this.applyFiltersEvent.emit({
      filters: this.requestFilters(),
      categories: Array.from(this.selectedCategoryIds()),
    });
  }

  selectAdditionalKeyFilter(keyFilter: SiaAIFiltersKeys) {
    if (keyFilter.selected) {
      this.removeAdditionalKeyFilter(keyFilter);
    } else {
      this.addAdditonalFilter(keyFilter);
    }
    this.keysAdditionalFilters.update((keysFilterChips) => {
      return keysFilterChips.map((x) => {
        if (x.key === keyFilter.key) {
          return { ...x, selected: !x.selected };
        }
        return x;
      });
    });
  }

  removeAdditionalKeyFilter(keyFilter: SiaAIFiltersKeys) {
    this.keysFilterChips.update((filtersChips: SiaGridFilterChip[]) => {
      return filtersChips.filter(
        (x) =>
          x.menuData.filter.key !== keyFilter.key ||
          x.menuData.filter.categoryId !== keyFilter.categoriesObject[0].id,
      );
    });
  }

  addAdditonalFilter(keyFilter: SiaAIFiltersKeys) {
    let chipType: 'array' | 'number' | 'text' | 'date' =
      this.retriveChipTypeByFilter(keyFilter);

    this.keysFilterChips.update((filtersChips: SiaGridFilterChip[]) => {
      return [
        ...filtersChips,
        {
          menuTriggerType: chipType,
          tooltip: keyFilter.description,
          label: keyFilter.description,
          category: keyFilter.categoriesObject[0].description,
          menuData: {
            filter: {
              key: keyFilter.key,
              isCategoryFilter: true,
              categoryId: keyFilter.categoriesObject[0].id,
              numberOperator: chipType === 'number' ? 'eq' : undefined,
              arrayFilter: chipType == 'array' ? [] : undefined,
            },
          },
        },
      ];
    });
  }
  private retriveChipTypeByFilter(keyFilter: SiaAIFiltersKeys) {
    let chipType: 'array' | 'number' | 'text' | 'date' = 'text';
    switch (keyFilter.type) {
      case 'text':
        chipType = 'text';
        break;
      case 'datetime':
      case 'date':
        chipType = 'date';
        break;
      case 'int':
      case 'decimal':
        chipType = 'number';
        break;
      case 'array':
        chipType = 'array';
        break;
    }
    return chipType;
  }

  /**
   * Apply filters to the one the user selected, replacing them if in conflict or concatenating them if not
   */
  applyAIFilters(aiFilterChips: any = null, aiCategories: any = null) {
    aiFilterChips = aiFilterChips ?? this.aiFilterChips();
    aiCategories = aiCategories ?? this.aiGeneratedFilters()?.categories ?? [];
    aiFilterChips.forEach((aiFilter: any) => {
      let filterChip = this.filtersChips().find(
        (x: any) =>
          x.menuData.filter.key === aiFilter.menuData.filter.key &&
          x.menuData.filter.categoryId == aiFilter.menuData.filter.categoryId,
      );
      if (!filterChip) {
        return;
      }
      switch (aiFilter.menuTriggerType) {
        case 'text':
          filterChip.menuData.filter.textFilter =
            aiFilter.menuData.filter.textFilter;
          break;
        case 'date':
          filterChip.menuData.filter.dateStart =
            aiFilter.menuData.filter.dateStart ??
            filterChip.menuData.filter.dateStart;
          filterChip.menuData.filter.dateEnd =
            aiFilter.menuData.filter.dateEnd ??
            filterChip.menuData.filter.dateEnd;
          break;
        case 'number':
          filterChip.menuData.filter.numberFilter =
            aiFilter.menuData.filter.numberFilter;
          filterChip.menuData.filter.numberOperator =
            aiFilter.menuData.filter.numberOperator ?? 'eq';
          break;
        case 'array':
          let currentArrayFilter = filterChip.menuData.filter.arrayFilter ?? [];
          filterChip.menuData.filter.arrayFilter = [
            ...(aiFilter.menuData.filter.arrayFilter ?? [])?.filter(
              (x: any) => !currentArrayFilter.includes(x),
            ),
            ...currentArrayFilter,
          ];
          break;
      }
    });

    this.selectedCategoryIds.update((categories) => {
      return new Set([...categories, ...(this.#aiSelectedCategories() ?? [])]);
    });
    this.filtersChips.set([...this.filtersChips()]);
    this.aiFilterChips.set([]);
    this.aiGeneratedFilters.set({ filters: [], categories: [] });
    this.setFilterChipsTextDescriptions(this.filtersChips);
  }

  chipsHaveSameValueBasedOnType(
    aiFilterChip: SiaGridFilterChip,
    filterChip: SiaGridFilterChip,
  ) {
    switch (aiFilterChip.menuTriggerType) {
      case 'text':
        return (
          aiFilterChip.menuData.filter.textFilter ===
          filterChip.menuData.filter.textFilter
        );
      case 'date':
        return (
          aiFilterChip.menuData.filter.dateStart ===
            filterChip.menuData.filter.dateStart &&
          aiFilterChip.menuData.filter.dateEnd ===
            filterChip.menuData.filter.dateEnd
        );
      case 'number':
        return (
          aiFilterChip.menuData.filter.numberFilter ===
            filterChip.menuData.filter.numberFilter &&
          aiFilterChip.menuData.filter.numberOperator ===
            filterChip.menuData.filter.numberOperator
        );
      case 'array':
        return aiFilterChip.menuData.filter.arrayFilter?.every(
          (x, i) => x === filterChip.menuData.filter.arrayFilter?.[i],
        );
    }
    return false;
  }
  matMenuClosed($event: string | void) {
    this.setAllFilterChipsTextDescriptions();
  }

  getFilteredArrayDataSource(dataSource: any[], searchText?: string): any[] {
    if (!dataSource || !searchText) {
      return dataSource || [];
    }
    const lowerSearch = searchText.toLowerCase();
    return dataSource.filter(
      (item) =>
        item &&
        item.description &&
        item.description.toLowerCase().includes(lowerSearch),
    );
  }
}

function getKeyIconByType(type: string): string {
  switch (type) {
    case 'datetime':
      return 'calendar_today';
    case 'int':
      return 'looks_one';
    case 'text':
      return 'text_fields';
    case 'date':
      return 'event';
    case 'decimal':
      return 'decimal_increase';
    default:
      return 'text_fields';
  }
}
